import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignUp, useSignInWithGoogle } from '../../hooks/queries/useAuth';
import { useProjects } from '../../hooks/queries/useProjects';
import { AuthForm } from './AuthForm';
import { logger } from '../../utils/logger';
import { supabase } from '../../lib/supabase';
import { secureApi } from '../../api/secure';

export function AuthSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');

  const { 
    mutateAsync: signUp, 
    isPending: isSigningUp 
  } = useSignUp();
  
  const { 
    mutateAsync: signInWithGoogle, 
    isPending: isGoogleSigningIn 
  } = useSignInWithGoogle();
  
  const { refetch: fetchProjects } = useProjects();

  const loading = isSigningUp || isGoogleSigningIn;

  // Get invitation details from URL
  const invitationId = searchParams.get('invitation');
  const projectId = searchParams.get('project');
  const emailParam = searchParams.get('email');

  // Debug on component mount
  useEffect(() => {
    // Log detailed component mount information including URL and search params
    logger.component('AuthSignup component mounted', {
      url: window.location.href,
      params: {
        invitationId,
        projectId,
        email: emailParam,
      },
      allSearchParams: Object.fromEntries([...searchParams]),
      pathname: window.location.pathname,
      search: window.location.search,
    });

    // Pre-fill email if provided in URL
    if (emailParam) {
      setEmail(emailParam);
    }

    // Log the invitation details
    if (invitationId && projectId) {
      logger.component('Handling invitation signup', {
        invitationId,
        projectId,
        email: emailParam,
      });
    }
  }, [invitationId, projectId, emailParam, searchParams]);

  const handleSubmit = async (email: string, password: string) => {
    logger.component('AuthSignup handling form submit', {
      email,
      passwordLength: password.length,
      hasInvitation: Boolean(invitationId),
    });
    
    setError(null);

    try {
      // Log additional information before signup
      logger.component('Attempting to sign up user', {
        email,
        hasInvitation: Boolean(invitationId),
        invitationId,
        projectId,
      });

      // Register the new user
      try {
        // Pass as object to avoid ambiguity with array format
        await signUp({ email, password });
        logger.component('User signup successful');
      } catch (signupError) {
        // Log detailed signup error
        logger.error('Detailed signup error', {
          error: signupError,
          message: signupError.message,
          code: signupError.code,
          details: signupError.details,
          status: signupError.status,
        });
        throw signupError;
      }

      // Wait for user setup to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Wait for user registration to complete
      // We need to wait for email confirmation and/or the database to fully update
      logger.component('Waiting for user registration to complete before processing invitations');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Get current user to ensure we have the latest state
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not available after signup');
      }

      logger.component('User registration confirmed, processing invitations', {
        userId: user.id,
        email: user.email,
      });

      // Process specific invitation if present in URL
      if (invitationId && projectId) {
        try {
          logger.component('Processing specific invitation from URL parameters', {
            invitationId,
            projectId,
          });

          const acceptResult = await secureApi.acceptInvitation(invitationId, projectId);
          logger.component('Specific invitation accepted via secureApi', acceptResult);
        } catch (specificInvitationError) {
          logger.error('Error processing specific invitation:', specificInvitationError);
          // Continue with auth flow even if specific invitation processing fails
        }
      }

      // Process ALL pending invitations for this user's email
      try {
        logger.component('Processing all pending invitations for user', { email: user.email });

        const processResult = await secureApi.processAllPendingInvitations(user.email);

        logger.component('All pending invitations processed', {
          count: processResult.processedCount,
          success: processResult.success,
        });

        if (processResult.processedCount > 0) {
          logger.component('User added to multiple projects', {
            projectCount: processResult.processedCount,
            projectIds: processResult.invitations?.map(inv => inv.project_id) || [],
          });
        }
      } catch (allInvitationsError) {
        logger.error('Error processing all pending invitations:', allInvitationsError);
        // Continue with auth flow even if bulk invitation processing fails
      }

      await fetchProjects();

      // Redirect to project if an invitation was involved, otherwise to project list
      if (projectId) {
        navigate(`/project/${projectId}`);
      } else {
        navigate('/project');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during signup';
      logger.error('AuthSignup error', { error: err, message: errorMessage });
      setError(errorMessage);
    }
  };

  const handleGoogleSignIn = async () => {
    logger.component('AuthSignup initiating Google sign-in');
    setError(null);

    try {
      // For Google sign-in, we'll pass the invitation parameters in the URL
      // so they can be processed after the OAuth flow completes
      const params: Record<string, string> = {};

      if (invitationId) {
        params.invitation = invitationId;
      }

      if (projectId) {
        params.project = projectId;
      }

      // Log the params we're passing
      logger.component('Signing in with Google and invitation params', params);

      // Pass the invitation params to be included in the redirect URL
      await signInWithGoogle({ additionalParams: params });

      // The user will be redirected to Google and then back to the callback URL
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      logger.error('Google sign-in failed', { error: err, message: errorMessage });
      setError(errorMessage);
    }
  };

  // Log on every render
  logger.component('AuthSignup rendering', { invitationId, projectId, email });

  try {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-4">
        <div className="w-full max-w-md bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">
              {invitationId ? 'Accept Invitation & Create Account' : 'Create your account'}
            </h2>
            {invitationId && (
              <p className="mt-2 text-gray-400 text-sm">
                Create an account to join this collaboration.
              </p>
            )}
          </div>

          <AuthForm
            mode="signup"
            onSubmit={handleSubmit}
            onGoogleSignIn={handleGoogleSignIn}
            loading={loading}
            error={error}
            initialEmail={email}
          />

          <div className="text-center pb-6">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    );
  } catch (renderError) {
    console.error('Error rendering AuthSignup component:', renderError);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-6">
        <h2 className="text-xl text-red-500 mb-4">Error Rendering Signup</h2>
        <p>{String(renderError)}</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Return to Home
        </button>
      </div>
    );
  }
}